import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// SVG charts for the full unit page (the prototype's chart() helper as a
// component). Every colour is a CSS class whose stroke / fill is a --v2-*
// token (UnitV2.css), so a chart follows the light / dark theme without a
// re-render. Tones: ink, sage, slate, amber, red, teal, grey.
//
// Accessible: the chart is a focusable figure (role="img" + label); ←/→
// (Home / End) step through the series points and the read-out is announced
// politely; a visually hidden table can carry every value (`table`).

const DEFAULT_PAD = { l: 52, r: 14, t: 12, b: 24 };
// Minimum vertical distance between two reference-line labels on one side.
export const REF_LABEL_GAP = 13;

/**
 * Label baselines for horizontal reference lines that never overlap (QA
 * BUG-7, as in the Branches charts): each label sits just above its line;
 * labels on the same side closer than `gap` are pushed apart, the lower one
 * going below its own line so no line crosses it; each label also has a halo
 * in the card colour (UnitV2.css). Input: [{ key, y (px), left }]; output: key → y.
 */
export const refLabelPositions = (items, gap = REF_LABEL_GAP) => {
    const out = {};
    ['left', 'right'].forEach((side) => {
        let prev = -Infinity;
        items
            .filter((it) => (side === 'left' ? Boolean(it.left) : !it.left))
            .sort((a, b) => a.y - b.y)
            .forEach((it) => {
                const want = it.y - 5;
                // Too close to the label above: drop below its own line (the
                // text then clears both lines), never onto it.
                const at = want >= prev + gap ? want : Math.max(prev + gap, it.y + REF_LABEL_GAP);
                out[it.key] = at;
                prev = at;
            });
    });
    return out;
};
const FALLBACK_WIDTH = 640;

/** Round ticks covering [lo, hi]. */
export const niceTicks = (lo, hi, target = 4) => {
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { lo: 0, hi: 1, ticks: [0, 1] };
    let a = lo;
    let b = hi;
    if (a === b) { a -= Math.abs(a) * 0.1 || 1; b += Math.abs(b) * 0.1 || 1; }
    const raw = (b - a) / target;
    const mag = 10 ** Math.floor(Math.log10(raw));
    const n = raw / mag;
    const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
    const y0 = Math.floor(a / step) * step;
    const y1 = Math.ceil(b / step) * step;
    const ticks = [];
    for (let v = y0; v <= y1 + step / 1e6; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
    return { lo: y0, hi: y1, ticks };
};

/** Width of an element, following resizes (jsdom: a fixed fallback). */
export function useMeasuredWidth(fallback = FALLBACK_WIDTH) {
    const ref = useRef(null);
    const [width, setWidth] = useState(fallback);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const read = () => { const w = Math.round(el.clientWidth); if (w > 0) setWidth(Math.max(280, w)); };
        read();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, width];
}

/**
 * A line / area / step chart with optional band, vertical bands, reference
 * lines and dots.
 *
 *   x, y        [min, max] domains
 *   series      [[x, y], …] the main line (tone: `lineTone`, default sage)
 *   step        draw the series as a step line
 *   area        fill under the series
 *   band        [[x, lo, hi], …] a shaded band
 *   vbands      [{ from, to, label }] shaded vertical ranges
 *   refs        [{ x | y, label, tone, dashed }] reference lines
 *   dots        [{ x, y, r, tone, hollow, tip }]
 *   xticks / yticks  [[value, label], …]
 *   readout(i)  text for the read-out at series point i (hover / keys)
 */
export function UnitChart({
    label, height = 200, pad = DEFAULT_PAD, x, y, series = [], step = false, area = false, band = null,
    vbands = [], refs = [], dots = [], xticks = [], yticks = [], readout, lineTone = 'sage', table = null,
    className,
}) {
    const [wrapRef, W] = useMeasuredWidth();
    const H = height;
    const P = { ...DEFAULT_PAD, ...pad };
    const [active, setActive] = useState(null);
    const [dotTip, setDotTip] = useState(null);
    const descId = `${useId().replace(/:/g, '')}-desc`;

    const span = (d) => (d[1] - d[0]) || 1;
    const sx = (v) => P.l + ((v - x[0]) / span(x)) * (W - P.l - P.r);
    const sy = (v) => H - P.b - ((v - y[0]) / span(y)) * (H - P.t - P.b);

    let path = '';
    series.forEach((p, i) => {
        if (step && i) path += `H${sx(p[0]).toFixed(1)}V${sy(p[1]).toFixed(1)}`;
        else path += `${i ? 'L' : 'M'}${sx(p[0]).toFixed(1)},${sy(p[1]).toFixed(1)}`;
    });
    if (step && series.length) path += `H${sx(x[1]).toFixed(1)}`;
    const areaPath = area && series.length
        ? `${path}L${sx(step ? x[1] : series[series.length - 1][0])},${sy(y[0])}L${sx(series[0][0])},${sy(y[0])}Z`
        : null;
    const bandPoints = band && band.length
        ? [...band.map((p) => `${sx(p[0])},${sy(p[2])}`), ...[...band].reverse().map((p) => `${sx(p[0])},${sy(p[1])}`)].join(' ')
        : null;

    const pointAt = (clientX, rect) => {
        if (!series.length) return null;
        const px = ((clientX - rect.left) / rect.width) * W;
        let best = 0;
        series.forEach((p, i) => { if (Math.abs(sx(p[0]) - px) < Math.abs(sx(series[best][0]) - px)) best = i; });
        return best;
    };
    const onMove = (e) => {
        const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
        const i = pointAt(e.clientX, svg.getBoundingClientRect());
        if (i != null) setActive(i);
    };
    const onKeyDown = (e) => {
        if (!series.length || !readout) return;
        const last = series.length - 1;
        const moves = {
            ArrowRight: () => Math.min(last, (active ?? -1) + 1),
            ArrowLeft: () => Math.max(0, (active ?? 1) - 1),
            Home: () => 0,
            End: () => last,
        };
        if (!moves[e.key]) return;
        e.preventDefault();
        setActive(moves[e.key]());
    };

    const refLabelY = refLabelPositions(
        refs.filter((r) => r.y != null).map((r) => ({ key: r.label, y: sy(r.y), left: r.left })),
    );

    const activePoint = active != null ? series[active] : null;
    const readoutText = active != null && readout ? readout(active) : null;
    const tip = dotTip || (activePoint && readoutText ? { x: activePoint[0], y: activePoint[1], text: readoutText } : null);

    return (
        <div className={['u2-chart', className].filter(Boolean).join(' ')} ref={wrapRef}>
            <svg
                viewBox={`0 0 ${W} ${H}`}
                role="img"
                aria-label={label}
                aria-describedby={table ? descId : undefined}
                tabIndex={readout ? 0 : undefined}
                onKeyDown={onKeyDown}
                onBlur={() => setActive(null)}
                focusable={readout ? 'true' : 'false'}
            >
                {yticks.map(([v, t]) => (
                    <g key={`y${v}`}>
                        <line className="u2-ch-grid" x1={P.l} x2={W - P.r} y1={sy(v)} y2={sy(v)} />
                        <text className="u2-ch-ax" x={P.l - 6} y={sy(v) + 3.5} textAnchor="end">{t}</text>
                    </g>
                ))}
                {xticks.map(([v, t]) => (
                    <text key={`x${v}`} className="u2-ch-ax" x={sx(v)} y={H - 6} textAnchor="middle">{t}</text>
                ))}
                {vbands.map((b) => (
                    <g key={`vb${b.from}`}>
                        <rect className="u2-ch-iqr" x={sx(b.from)} y={P.t} width={Math.max(0, sx(b.to) - sx(b.from))} height={H - P.t - P.b} />
                        {b.label && <text className="u2-ch-ax" x={(sx(b.from) + sx(b.to)) / 2} y={P.t + 11} textAnchor="middle">{b.label}</text>}
                    </g>
                ))}
                {bandPoints && <polygon className="u2-ch-band" points={bandPoints} />}
                {areaPath && <path className={`u2-ch-area u2-tone-${lineTone}`} d={areaPath} />}
                {refs.map((r) => (r.y != null ? (
                    <g key={`ry${r.label}`} className={`u2-ch-ref u2-tone-${r.tone || 'grey'}${r.dashed === false ? ' is-solid' : ''}`}>
                        <line x1={P.l} x2={W - P.r} y1={sy(r.y)} y2={sy(r.y)} />
                        <text x={r.left ? P.l + 6 : W - P.r} y={refLabelY[r.label] ?? sy(r.y) - 5} textAnchor={r.left ? 'start' : 'end'}>{r.label}</text>
                    </g>
                ) : (
                    <g key={`rx${r.label}`} className={`u2-ch-ref u2-tone-${r.tone || 'grey'}`}>
                        <line x1={sx(r.x)} x2={sx(r.x)} y1={P.t} y2={H - P.b} />
                        <text x={sx(r.x) + 5} y={H - P.b - 6}>{r.label}</text>
                    </g>
                )))}
                <line className="u2-ch-base" x1={P.l} x2={W - P.r} y1={sy(y[0])} y2={sy(y[0])} />
                {path && <path className={`u2-ch-ln u2-tone-${lineTone}`} d={path} />}
                {dots.map((d, i) => (
                    <circle
                        // eslint-disable-next-line react/no-array-index-key
                        key={`d${i}`}
                        className={`u2-ch-dot u2-tone-${d.tone || lineTone}${d.hollow ? ' is-hollow' : ''}`}
                        cx={sx(d.x)}
                        cy={sy(d.y)}
                        r={d.r || 4.5}
                        onMouseEnter={d.tip ? () => setDotTip({ x: d.x, y: d.y, text: d.tip }) : undefined}
                        onMouseLeave={d.tip ? () => setDotTip(null) : undefined}
                    >
                        {d.tip && <title>{d.tip}</title>}
                    </circle>
                ))}
                {activePoint && (
                    <g className="u2-ch-hov" aria-hidden="true">
                        <line x1={sx(activePoint[0])} x2={sx(activePoint[0])} y1={P.t} y2={H - P.b} />
                        <circle cx={sx(activePoint[0])} cy={sy(activePoint[1])} r={5} />
                    </g>
                )}
                {readout && series.length > 0 && (
                    <rect
                        className="u2-ch-hit"
                        x={P.l}
                        y={P.t}
                        width={Math.max(0, W - P.l - P.r)}
                        height={Math.max(0, H - P.t - P.b)}
                        onMouseMove={onMove}
                        onMouseLeave={() => setActive(null)}
                    />
                )}
            </svg>
            {tip && (
                <div
                    className="u2-ttip"
                    aria-hidden="true"
                    style={{ left: `${(sx(tip.x) / W) * 100}%`, top: `${(sy(tip.y) / H) * 100}%` }}
                >
                    {tip.text}
                </div>
            )}
            <span className="v2-sr" aria-live="polite">{readoutText || ''}</span>
            {/* A table ignores width: 1px and grows to its content, so it sits
                in a clipped box (QA BUG-3: the page panned sideways). Rows are
                keyed by index: several points can share a date (QA BUG-8). */}
            {table && (
                <div className="v2-sr u2-sr-box">
                    <table id={descId}>
                        <caption>{label}</caption>
                        <tbody>
                            {table.map((row, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <tr key={`${i}-${row[0]}`}><th scope="row">{row[0]}</th><td>{row[1]}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

UnitChart.propTypes = {
    label: PropTypes.string.isRequired,
    height: PropTypes.number,
    pad: PropTypes.object,
    x: PropTypes.arrayOf(PropTypes.number).isRequired,
    y: PropTypes.arrayOf(PropTypes.number).isRequired,
    series: PropTypes.array,
    step: PropTypes.bool,
    area: PropTypes.bool,
    band: PropTypes.array,
    vbands: PropTypes.array,
    refs: PropTypes.array,
    dots: PropTypes.array,
    xticks: PropTypes.array,
    yticks: PropTypes.array,
    readout: PropTypes.func,
    lineTone: PropTypes.string,
    table: PropTypes.array,
    className: PropTypes.string,
};

/** A small trend line under a figure (token colour via `tone`). */
export function Sparkline({ values, tone = 'sage', height = 24, label }) {
    if (!Array.isArray(values) || values.length === 0) return null;
    const w = 100;
    const h = height;
    const p = 2;
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    const stepX = values.length > 1 ? (w - p * 2) / (values.length - 1) : 0;
    const pts = values.map((v, i) => [p + i * stepX, h - p - ((v - min) / range) * (h - p * 2)]);
    const line = pts.map((pt, i) => `${i ? 'L' : 'M'}${pt[0].toFixed(1)} ${pt[1].toFixed(1)}`).join(' ');
    const areaD = `${line} L${pts[pts.length - 1][0].toFixed(1)} ${h - p} L${pts[0][0].toFixed(1)} ${h - p} Z`;
    return (
        <svg
            className={`u2-spark u2-tone-${tone}`}
            width="100%"
            height={h}
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="none"
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : 'true'}
            focusable="false"
        >
            <path className="u2-spark-area" d={areaD} />
            <path className="u2-spark-ln" d={line} />
        </svg>
    );
}

Sparkline.propTypes = {
    values: PropTypes.arrayOf(PropTypes.number),
    tone: PropTypes.string,
    height: PropTypes.number,
    label: PropTypes.string,
};
